import { Form, InputNumber, Radio, Select } from 'antd';
import { maxFontSize, minFontSize, searchInputWidth, unitName } from '../../../constants/gridConfig';
import { useStore } from '../../../store/store';
import FontPicker from '../../common/FontPicker';
import { BlockTextStyle } from '../../common/TitleConfiguration';
import { ColorPaletteButton } from '../../common/ColorPaletteButton';
import { defaultConfiguration } from '../../../constants/constants';

const selector = ({
  configuration,
  setConfiguration,
  summaryConfiguration,
  setSummaryConfiguration,
  paysageMode,
  setPaysageMode,
  currentTemplate,
}) => ({
  configuration,
  setConfiguration,
  summaryConfiguration,
  setSummaryConfiguration,
  paysageMode,
  setPaysageMode,
  currentTemplate,
});

// Les documents antérieurs n'ont ni emplacement, ni police, ni écart avec le
// haut : pied de page, Helvetica (le PDF la prend quand la police est vide) et
// l'écart par défaut.
export const DEFAULT_TOP = defaultConfiguration.pageNumberStyle.position.top;
export const DEFAULT_FONT_FAMILY = 'Helvetica';

export const TabsPages = () => {
  const {
    configuration,
    setConfiguration,
  } = useStore(selector);
  const handleChangePageNumberFontColor = (selectedColor) => {
    setConfiguration({
      pageNumberStyle: {
        ...configuration.pageNumberStyle,
        color: configuration.colors.find((color) => color.label === selectedColor),
      },
    });
  };

  const handleChangeFontSize = (value) => {
    setConfiguration({
      pageNumberStyle: { ...configuration.pageNumberStyle, size: value },
    });
  };
  const handleChangePageNbFormat = (value) => {
    setConfiguration({
      pageNumberStyle: {
        ...configuration.pageNumberStyle,
        format: value
      },
    });
  };
  const handleChangePositionBottom = (value) => {
    setConfiguration({
      pageNumberStyle: {
        ...configuration.pageNumberStyle, position: {
          ...configuration.pageNumberStyle.position,
          bottom: value
        }
      },
    });
  };
  const handleChangePositionTop = (value) => {
    setConfiguration({
      pageNumberStyle: {
        ...configuration.pageNumberStyle, position: {
          ...configuration.pageNumberStyle.position,
          top: value
        }
      },
    });
  };
  const handleChangePositionRight = (value) => {
    setConfiguration({
      pageNumberStyle: {
        ...configuration.pageNumberStyle, position: {
          ...configuration.pageNumberStyle.position,
          right: value
        }
      },
    });
  };

  const handleChangePlacement = (e) => {
    setConfiguration({
      pageNumberStyle: {
        ...configuration.pageNumberStyle,
        placement: e.target.value,
      },
    });
  };

  const handleChangeFontFamily = (fontFamily) => {
    setConfiguration({
      pageNumberStyle: {
        ...configuration.pageNumberStyle,
        fontFamily,
      },
    });
  };

  // Les boutons G/I/S basculent au clic : la valeur en cours dit l'état, il n'y
  // a pas de case à lire.
  const toggleTextStyle = (property, activeValue) => () => {
    setConfiguration({
      pageNumberStyle: {
        ...configuration.pageNumberStyle,
        [property]: configuration.pageNumberStyle[property] ? '' : activeValue,
      },
    });
  };

  const changeTextStyleHandler = {
    fontWeight: toggleTextStyle('fontWeight', 'bold'),
    fontStyle: toggleTextStyle('fontStyle', 'italic'),
    textDecoration: toggleTextStyle('textDecoration', 'underline'),
  };


  const isInHeader = configuration.pageNumberStyle.placement === 'header';

  return (
    <>
      {/* Pas de `name` sur les nouveaux champs : pilotés par le store (cf. titleTab). */}
      <Form.Item label='Emplacement'>
        <Radio.Group
          optionType='button'
          value={isInHeader ? 'header' : 'footer'}
          onChange={handleChangePlacement}
          options={[
            { value: 'footer', label: 'Pied de page' },
            { value: 'header', label: 'En-tête' },
          ]}
        />
      </Form.Item>
      <Form.Item
        name='page-format'
        label={'Format du numéro de page'}
      >
        <Select
          onChange={handleChangePageNbFormat}
          defaultValue={configuration.pageNumberStyle.format}
          style={{ width: searchInputWidth }}
          options={[
            { value: 'simple', label: 'N° de page (seulement)' },
            { value: 'total', label: 'N° de page / Nb total de pages' },
            { value: 'empty', label: 'Sans numéro de page' },
          ]}
        >
        </Select>
      </Form.Item>
      <Form.Item label='Police'>
        <FontPicker
          allowClear
          placeholder={`${DEFAULT_FONT_FAMILY} (par défaut)`}
          forcedValue={configuration.pageNumberStyle.fontFamily || undefined}
          onChange={handleChangeFontFamily}
        />
      </Form.Item>
      <Form.Item
        name='page-font-size'
        label={'Taille de police'}
      >
        <InputNumber
          min={minFontSize}
          max={maxFontSize}
          defaultValue={configuration.pageNumberStyle.size}
          onChange={handleChangeFontSize}
        />
      </Form.Item>
      {/* Mêmes boutons G/I/S que les titres et les textes : trois cases à cocher
          libellées tenaient la largeur d'une ligne pour trois réglages. */}
      <BlockTextStyle
        rules={configuration.pageNumberStyle}
        changeHandler={changeTextStyleHandler}
      />
      <Form.Item label='Couleur du numéro'>
        {/* Bouton à palette de la barre de style (le « A » souligné de la
            couleur choisie), pas un sélecteur large : un seul numéro à colorer,
            et la teinte se lit sur le bouton. Le numéro n'est jamais
            transparent — il disparaîtrait du PDF sans rien dire. */}
        <ColorPaletteButton
          title='Couleur du numéro'
          disableTransparent
          value={configuration.pageNumberStyle.color ?? configuration.baseFontColor}
          colors={configuration.colors}
          onChange={handleChangePageNumberFontColor}
        />
      </Form.Item>
      {isInHeader ? (
        <Form.Item label='Espacement avec le haut de la page'>
          <InputNumber
            min={0}
            addonAfter={unitName}
            style={{ width: 150 }}
            value={configuration.pageNumberStyle.position.top ?? DEFAULT_TOP}
            onChange={handleChangePositionTop}
          />
        </Form.Item>
      ) : (
        <Form.Item
          name='position-bottom'
          label={'Espacement avec le bas de la page'}
        >
          <InputNumber
            min={0}
            addonAfter={unitName}
            style={{ width: 150 }}
            defaultValue={configuration.pageNumberStyle.position.bottom}
            onChange={handleChangePositionBottom}
          />
        </Form.Item>
      )}
      <Form.Item
        name='position-right'
        label={'Espacement avec la droite de la page'}
      >
        <InputNumber
          min={0}
          addonAfter={unitName}
          style={{ width: 150 }}
          defaultValue={configuration.pageNumberStyle.position.right}
          onChange={handleChangePositionRight}
        />
      </Form.Item>
    </>
  );
};
